import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';

export interface ConfirmDialogProps {
  /** Drives mount/unmount of the portal body. */
  open: boolean;
  /** Heading line — short, action-shaped (e.g. `Remove currency?`). */
  title: ReactNode;
  /** Body copy. Plain string is fine; `ReactNode` for richer content. */
  message?: ReactNode;
  /** Confirm-button label. Defaults to `Confirm`. */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to `Cancel`. */
  cancelLabel?: string;
  /**
   * Visual tone. `danger` colours the confirm button red and shows a warning
   * glyph in the header — use for destructive actions (delete, force-unlink,
   * etc.). `default` is the neutral blue confirm.
   */
  tone?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
  /** Disables the confirm button (e.g. while a mutation is in flight). */
  busy?: boolean;
}

/**
 * App-wide confirmation modal. Mount as a sibling alongside whatever fired
 * the destructive action — the component portals itself to `document.body`
 * so any z-index parents don't clip the backdrop.
 *
 * Dismiss surfaces:
 *  - Cancel button
 *  - Escape key
 *  - Backdrop click
 *
 * The confirm button auto-focuses when the dialog opens so a mid-typing
 * keyboard user doesn't lose where they are; `Enter` activates it.
 *
 * Replaces the browser `window.confirm()` we were using in destructive
 * flows — same shape (yes/no), nicer presentation, and consistent across
 * the suite.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Escape closes; auto-focus the confirm button on open. Listener is scoped
  // to the open lifetime so we don't leak handlers across mount cycles.
  useEffect(() => {
    if (!open) return;
    confirmButtonRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const confirmTone =
    tone === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
      : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';

  return createPortal(
    <div
      role="presentation"
      // Backdrop click fires onCancel; the modal panel stops propagation so
      // an inside-click doesn't leak through and dismiss the dialog.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className="w-full max-w-sm rounded-md bg-white p-4 shadow-xl"
      >
        <div className="mb-3 flex items-start gap-2">
          {tone === 'danger' && (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"
              aria-hidden
            >
              <AlertTriangle className="h-4 w-4" />
            </span>
          )}
          <h3 id="confirm-dialog-title" className="text-sm font-semibold text-slate-900">
            {title}
          </h3>
        </div>

        {message && <div className="mb-4 text-sm text-slate-600">{message}</div>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={cn(
              'rounded-sm px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 disabled:opacity-50',
              confirmTone,
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
